import { CourseAuthor } from '../../apis/api-client';
import { getPortraitObjectPosition } from '../../utils/courseAuthor';
import EmptyTabMessage from './EmptyTabMessage';
import { Box, Button, Card, Typography } from '../ui';

interface CourseInstructorTabProps {
  author: CourseAuthor | null;
  /** Admin edit link — surfaces an "Assign instructor" CTA in the empty
   *  state when present. */
  editLink?: string;
}

/**
 * CourseInstructorTab
 *
 * The "About the Instructor" tab on the learner course-detail page:
 * portrait + bio + credentials + optional pull quote. Purely
 * presentational — extracted from LearnerCourseDetailLayout.
 */
export default function CourseInstructorTab({ author, editLink }: CourseInstructorTabProps) {
  return (
    <Card sx={{ p: { xs: 2, md: 4 } }}>
      {!author ? (
        <Box sx={{ py: 2 }}>
          <EmptyTabMessage message="No instructor is assigned to this course yet." />
          {editLink && (
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                color="primary"
                href={editLink}
                sx={{
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Assign instructor →
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '300px 1fr' },
            gap: { xs: 3, md: 5 },
            alignItems: 'start',
          }}
        >
          {/* Portrait column. Wraps the image in its own card with a soft
              cyan dotted accent that peeks behind the corner. */}
          <Box
            sx={{
              position: 'relative',
              width: { xs: 220, md: '100%' },
              maxWidth: { xs: 220, md: 300 },
              mx: { xs: 'auto', md: 0 },
            }}
          >
            {/* Dotted-grid backdrop, top-left of the portrait. */}
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                top: -14,
                left: -14,
                width: 110,
                height: 110,
                backgroundImage:
                  'radial-gradient(rgba(var(--brand-rgb), 0.65) 1.4px, transparent 1.6px)',
                backgroundSize: '12px 12px',
                opacity: 0.7,
                zIndex: 0,
              }}
            />
            {/* Dotted-grid backdrop, bottom-right mirror. */}
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                bottom: -14,
                right: -14,
                width: 110,
                height: 110,
                backgroundImage:
                  'radial-gradient(rgba(var(--brand-rgb), 0.5) 1.4px, transparent 1.6px)',
                backgroundSize: '12px 12px',
                opacity: 0.6,
                zIndex: 0,
              }}
            />
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                aspectRatio: '3 / 4',
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: 'grey.100',
                boxShadow: '0 18px 40px -22px rgba(15,72,90,0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {author.portraitUrl ? (
                <Box
                  component="img"
                  src={author.portraitUrl}
                  alt={author.name}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: getPortraitObjectPosition(author),
                    display: 'block',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    fontSize: '5rem',
                    fontWeight: 700,
                    color: 'primary.main',
                    opacity: 0.7,
                  }}
                >
                  {author.name.charAt(0).toUpperCase()}
                </Box>
              )}
            </Box>
          </Box>

          {/* Bio column. Eyebrow → name → credentials → divider →
              paragraphs → optional pull quote. */}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 2,
                  bgcolor: 'primary.main',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                }}
              >
                Course Instructor
              </Typography>
            </Box>

            <Typography
              variant="h3"
              sx={{
                mb: author.credentials ? 1 : 2,
                wordBreak: 'break-word',
                lineHeight: 1.15,
              }}
            >
              {author.name}
            </Typography>

            {author.credentials && (
              <Typography
                variant="h6"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  mb: 2,
                  letterSpacing: 0,
                }}
              >
                {author.credentials}
              </Typography>
            )}

            {/* Hairline divider — separates the title block from the bio. */}
            <Box
              sx={{
                height: '1px',
                background:
                  'linear-gradient(to right, rgba(var(--brand-rgb), 0.55) 0%, rgba(var(--brand-rgb), 0.15) 60%, transparent 100%)',
                mb: 3,
              }}
            />

            {author.bio ? (
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                  lineHeight: 1.75,
                  fontSize: { xs: '1rem', md: '1.05rem' },
                  color: 'text.primary',
                }}
              >
                {author.bio}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No bio has been added to this instructor's template yet.
              </Typography>
            )}

            {author.quote && (
              <Box
                sx={{
                  mt: 4,
                  p: { xs: 2.5, md: 3 },
                  borderRadius: 2,
                  bgcolor: 'rgba(var(--brand-rgb), 0.10)',
                  position: 'relative',
                }}
              >
                {/* Oversized opening quote glyph as a visual anchor. */}
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: 4,
                    left: 14,
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontSize: '4rem',
                    lineHeight: 1,
                    color: 'primary.main',
                    opacity: 0.45,
                    pointerEvents: 'none',
                  }}
                >
                  &ldquo;
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: 'italic',
                    lineHeight: 1.7,
                    pl: { xs: 4, md: 5 },
                    color: 'text.primary',
                  }}
                >
                  {author.quote}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 1.5,
                    pl: { xs: 4, md: 5 },
                    color: 'primary.main',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                  }}
                >
                  — {author.name}
                  {author.credentials ? `, ${author.credentials}` : ''}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Card>
  );
}
